/**
 * @file     secureFetch.ts
 * @readme   ./CLAUDE.md
 * @purpose  Wrapper around fetch that automatically attaches CSRF tokens to POST requests to /api/ routes.
 */

import { fetchCsrfToken, invalidateCsrfToken } from '@/hooks/useCsrf';

/**
 * Drop-in replacement for fetch() that adds x-csrf-token header
 * to all POST/PUT/DELETE/PATCH requests to /api/ routes.
 *
 * On a 403 response, busts the in-memory CSRF cache and retries once —
 * covers the case where a cached token outlived its server-side validity
 * (e.g. across sign-in transitions in the same tab).
 */
export async function secureFetch(
  input: string | URL | Request,
  init?: RequestInit,
): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  const method = (init?.method ?? 'GET').toUpperCase();

  if (!['POST', 'PUT', 'DELETE', 'PATCH'].includes(method) || !url.startsWith('/api/')) {
    return fetch(input, init);
  }

  const send = async () => {
    const token = await fetchCsrfToken();
    const headers = new Headers(init?.headers);
    headers.set('x-csrf-token', token);
    return fetch(input, { ...init, headers });
  };

  const res = await send();
  if (res.status !== 403) return res;

  invalidateCsrfToken();
  return send();
}
